import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PlayerStatusStrip } from '../ui/PlayerStatus'; // v22 BOLT-116
import { dedupeSlots, type StatusSlot } from '../../shared/playerStatus';
import { useGame } from '../state/SocketProvider';
import { MapView } from '../map/MapView';
import { buildCutPreview } from '../map/cutPreview';
import { babelLang, bt } from '../../shared/babel/strings';
import type { BabelLang } from '../../shared/protocol';
import { Cockpit } from '../round/Cockpit';
import { CardRail } from '../round/CardRail';
import { CardSheet } from '../round/CardSheet';
import { ThermoHud } from '../round/ThermoHud';
import { HiderHand } from '../round/HiderHand';
import { HiderCardSheet } from '../round/HiderCardSheet';
import { IncomingPanel } from '../round/IncomingPanel';
import { PhotoPrompt } from '../round/PhotoPrompt';
import { PhotoViewer } from '../round/PhotoViewer';
import { VoiceMic } from '../round/VoiceMic';
import { CurseBanner } from '../round/CurseBanner';
import { StopBanner } from '../round/StopBanner';
import { FreezeLockBanner } from '../round/FreezeLockBanner';
import { PortalBanner } from '../round/PortalBanner';
import { PortalPlacement } from './PortalPlacement';
import { isPortalCard } from '../../shared/hiderdeck';
import { simStore, useSim } from '../state/sim';
import { PenaltyWarning } from '../round/PenaltyWarning';
import type { HiderCard } from '../../shared/protocol';
import { getFreshFix } from '../net/geo';
import { playWhistle, playConfirmTone } from '../net/alert';
import { useWakeLock } from '../net/wakelock';
import { formatCountdown } from '../util/format';
import { useServerClock } from '../util/clock';
import type { BannerInfo, CardMenuItem, PublicState } from '../../shared/protocol';

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));

export function Round() {
  const { state, send, sendReliable, subscribe } = useGame();
  const sim = useSim();
  useWakeLock(); // keep the screen awake during a round (real-device, §6.7)
  const [card, setCard] = useState<CardMenuItem | null>(null);
  const [hiderCard, setHiderCard] = useState<HiderCard | null>(null);
  const [placingPortal, setPlacingPortal] = useState<HiderCard | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [banner, setBanner] = useState<BannerInfo | null>(null);
  const bannerId = useRef<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout>>();
  // (v11 §2A: the alert transition diff moved up to App.tsx so the round arc
  // still alerts across screen mounts — lobby→headstart, active→intermission.)

  // v11 §2G: the live cut preview — from card selection until it resolves or
  // is vetoed (pending plays + the thermo walk come from server state; the
  // pre-play selection is local). Recomputes as the seeker's pin moves.
  // BOLT-045: the synthetic thermo direction — random per card selection,
  // stable across renders (no per-frame flicker), re-rolled on re-select.
  const thermoBearing = useMemo(() => Math.random() * 360, [card?.id]);
  const preview = useMemo(() => buildCutPreview(state, card, thermoBearing), [state, card, thermoBearing]);

  const showToast = useCallback((s: string) => {
    setToast(s);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  }, []);

  // transient banner: surface each new resolution, framed per role. v5.5 §4: the
  // two "done to you" results (vetoed / a curse landing) dwell longer (~7s) and
  // are tap-to-dismiss, so a heads-down player still catches them; others keep 4s.
  const bId = state?.banner?.id;
  useEffect(() => {
    const b = state?.banner;
    if (b && b.id !== bannerId.current) {
      bannerId.current = b.id;
      setBanner(b);
      const id = setTimeout(() => setBanner(null), isStickyBanner(b) ? 7000 : 4000);
      return () => clearTimeout(id);
    }
  }, [bId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () =>
      subscribe((m) => {
        if (m.t === 'found_result') showToast(m.inRange ? 'IN RANGE - CAUGHT' : 'NOT IN RANGE');
        else if (m.t === 'card_ack' && !m.accepted && m.reason) showToast(m.reason.toUpperCase());
        else if (m.t === 'error') showToast(m.message.toUpperCase());
        else if (m.t === 'airhorn') {
          // v16 BOLT-072: sound the horn NOW; ack only if playback actually
          // started — a failed ack times out into the seeker's refund.
          if (playWhistle()) sendReliable({ t: 'airhorn_ack', playId: m.playId, idem: uuid() });
        }
      }),
    [subscribe, showToast, sendReliable],
  );

  if (!state) return null;
  const role = state.you.role;
  const phase = state.phase;

  const playCard = async (c: CardMenuItem) => {
    try {
      const fix = await getFreshFix();
      sendReliable({ t: 'play_card', cardId: c.id, at: { lat: fix.lat, lng: fix.lng }, idem: uuid() });
      setCard(null);
    } catch {
      setCard(null);
      showToast('NO GPS - CARD NOT PLAYED');
    }
  };
  const found = async () => {
    try {
      const fix = await getFreshFix();
      sendReliable({ t: 'found_attempt', at: { lat: fix.lat, lng: fix.lng }, acc: fix.acc, idem: uuid() });
    } catch {
      showToast('NO GPS FIX');
    }
  };
  const stopThermo = () => {
    if (state.thermo) send({ t: 'thermo_stop', cardId: state.thermo.cardId, idem: uuid() });
  };
  const lockAnchor = async () => {
    try {
      const fix = await getFreshFix();
      send({ t: 'lock_anchor', at: { lat: fix.lat, lng: fix.lng } });
    } catch {
      showToast('NO GPS FIX');
    }
  };
  // v13 BOLT-035: ending a run takes TWO distinct interactions — arm, then
  // confirm (auto-disarms) — never a single accidental tap.
  const [confirmEnd, setConfirmEnd] = useState(false);
  const confirmEndTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const endRun = () => {
    if (!confirmEnd) {
      setConfirmEnd(true);
      confirmEndTimer.current = setTimeout(() => setConfirmEnd(false), 4000);
      return;
    }
    if (confirmEndTimer.current) clearTimeout(confirmEndTimer.current);
    setConfirmEnd(false);
    send({ t: 'end_round' });
  };
  const pickHider = (c: HiderCard) => {
    if ((state?.mustDiscard ?? 0) > 0) {
      send({ t: 'discard', cardIds: [c.id], idem: uuid() });
      showToast('DROPPED');
    } else setHiderCard(c);
  };
  const playHider = (c: HiderCard) => {
    setHiderCard(null);
    if (isPortalCard(c)) {
      setPlacingPortal(c); // opens placement; the card is consumed on Arm
      return;
    }
    sendReliable({ t: 'play_hider_card', cardId: c.id, idem: uuid() });
  };
  const vetoPlay = (playId: string) => sendReliable({ t: 'veto', playId, idem: uuid() });
  // Upload a photo and resolve when the server acks it (so the prompt can show
  // sent/error). Reliable send → survives a drop; the server dedupes by idem.
  const uploadPhoto = (cardId: string, dataUrl: string): Promise<boolean> =>
    new Promise((resolve) => {
      const idem = uuid();
      let settled = false;
      const finish = (ok: boolean) => {
        if (settled) return;
        settled = true;
        off();
        clearTimeout(timer);
        resolve(ok);
      };
      const off = subscribe((m) => {
        if (m.t === 'hider_ack' && m.idem === idem) {
          if (!m.accepted && m.reason) showToast(m.reason.toUpperCase());
          finish(!!m.accepted);
        }
      });
      const timer = setTimeout(() => finish(false), 20_000);
      sendReliable({ t: 'upload_photo', cardId, dataUrl, idem });
    });

  return (
    <div className="app">
      <MapView state={state} preview={preview} onMapClick={sim.enabled ? (p) => simStore.setPos(p) : undefined} />

      {state.you.isHost && (
        <button
          className="btn btn--danger admin-btn"
          style={{ width: 'auto' }}
          data-testid="end-run"
          data-arming={confirmEnd || undefined}
          onClick={endRun}
        >
          {confirmEnd ? 'Confirm end?' : 'End game'}
        </button>
      )}

      <div className="hud-top">
        {phase === 'headstart' ? <HeadStartChip endsAt={state.timer.headStartEndsAt} serverNow={state.timer.serverNow} /> : <Cockpit state={state} />}
        <StaleBadge state={state} />
        {/* v22 BOLT-116: everyone's connection / GPS / battery, mid-round.
            COLLAPSED by default: the cockpit is for playing, not monitoring. */}
        <PlayerStatusStrip
          slots={dedupeSlots([
            state.players.hider, state.players.seeker, state.players.teamA, state.players.teamB,
          ] as (StatusSlot | null | undefined)[]).map((slot) => ({ slot, you: slot.name === state.you.name }))}
        />
        {banner && <Banner b={banner} lang={babelLang(state)} onDismiss={() => setBanner(null)} />}
        <StopBanner state={state} />
        <FreezeLockBanner state={state} />
        <CurseBanner state={state} />
        <PortalBanner state={state} />
        <PenaltyWarning state={state} />
        <IncomingPanel state={state} onVeto={vetoPlay} />
        {role === 'hider' &&
          phase === 'active' &&
          (state.photoWindows ?? [])
            .slice()
            .sort((a, b) => a.endsAt - b.endsAt)
            .map((w) => <PhotoPrompt key={w.cardId} win={w} serverNow={state.timer.serverNow} onSend={uploadPhoto} />)}
        {phase === 'active' && (
          <PhotoViewer
            photos={state.photos ?? []}
            windows={state.photoWindows ?? []}
            serverNow={state.timer.serverNow}
            isSeeker={role === 'seeker'}
          />
        )}
        {/* v11 §2D: hold-to-talk — one mic, both roles, release sends */}
        {role && (
          <VoiceMic
            onSend={(dataUrl) => sendReliable({ t: 'send_voice', dataUrl, idem: uuid() })}
            onFail={() => showToast('VOICE NOTE FAILED')}
          />
        )}
      </div>

      <div className="dock">
        {role === 'seeker' && phase === 'active' && (
          <>
            {state.thermo && <ThermoHud thermo={state.thermo} serverNow={state.timer.serverNow} onStop={stopThermo} />}
            <CardRail
              lang={babelLang(state)} // v16 BOLT-071
              cards={state.deck ?? []}
              onPick={setCard}
              locked={
                // v15 BOLT-051: a pending question locks the rail (the veto
                // window IS the pacing) · BOLT-054: only SILENCE ('snail')
                // locks it now — Hold-Up is a movement freeze, cards flow.
                // v20 BOLT-097: a Freeze LOCK-OUT (left the ring at expiry) locks it.
                state.pendingPlays.length > 0 ||
                state.activeCurse?.kind === 'snail' ||
                state.portal?.state === 'triggered' ||
                !!state.freezeLocked
              }
            />
            <button className="found-fab" data-testid="found-btn" onClick={found}>
              {bt(babelLang(state), 'found')}
            </button>
            {/* v22 BOLT-114: in PRACTICE you almost always want to quit before
                catching the bot, so burying it in the host menu is friction.
                In a normal game it stays buried on purpose: BOLT-035 pulled
                "End game" off the play surface precisely because it sat next
                to Found! and the cards, and was a real mis-tap hazard. */}
            {state.practice && (
              <button className="end-practice" data-testid="end-practice" onClick={() => send({ t: 'end_round' })}>
                End Practice
              </button>
            )}
          </>
        )}

        {role === 'seeker' && phase === 'headstart' && (
          <div className="hider-dock">
            <span className="hider-note">Hider is bolting. Cards unlock when the round starts.</span>
          </div>
        )}

        {role === 'hider' && phase === 'headstart' && (
          <div className="hider-dock">
            <span className="hider-note">Go! Find a hiding spot. Tap I'm hidden when you're set.</span>
            {/* v16 BOLT-072 (web leg), demoted by v17 BOLT-076: the web cannot
                read media volume, so the hider's own ears stay the probe — but
                it is a NAG now, not a gate. The Airhorn is dealt either way;
                confirming just reports telemetry (and unlocks WebAudio). */}
            <SoundCheck onConfirm={() => send({ t: 'airhorn_ready', ready: true })} />
            <button className="btn btn--signal btn--lg" data-testid="lock-anchor" onClick={lockAnchor}>
              I'm hidden
            </button>
          </div>
        )}

        {role === 'hider' && phase === 'active' && (
          <div className="hider-dock">
            <div className="row between" style={{ width: '100%' }}>
              <span className="banked" data-testid="banked">
                Banked +{Math.round(state.bankedMin ?? 0)}m
              </span>
              {state.thermo ? (
                <ThermoWatch endsAt={state.thermo.endsAt} serverNow={state.timer.serverNow} />
              ) : (
                <span className="hider-note" style={{ margin: 0 }}>
                  You're hidden
                </span>
              )}
            </div>
            {(state.mustDiscard ?? 0) > 0 && (
              <div className="discard-prompt" data-testid="discard-prompt">
                Hand full - tap {state.mustDiscard} to drop
              </div>
            )}
            <HiderHand
              cards={state.hand ?? []}
              onPick={pickHider}
              discardMode={(state.mustDiscard ?? 0) > 0}
              curseActive={!!state.activeCurse}
              settings={state.settings}
            />
          </div>
        )}
      </div>

      {toast && (
        <div className="toast" data-testid="toast">
          {toast}
        </div>
      )}
      {card && <CardSheet card={card} lang={babelLang(state)} onPlay={playCard} onCancel={() => setCard(null)} />}
      {hiderCard && <HiderCardSheet card={hiderCard} onPlay={playHider} onCancel={() => setHiderCard(null)} />}
      {placingPortal && (
        <PortalPlacement
          state={state}
          onConfirm={(entry, exit) => {
            sendReliable({ t: 'place_portal', cardId: placingPortal.id, entry, exit, idem: uuid() });
            setPlacingPortal(null);
          }}
          onCancel={() => setPlacingPortal(null)}
        />
      )}
    </div>
  );
}

/** "Done to you" results (a veto, or a curse landing) — these get the longer
 *  dwell + tap-to-dismiss (§4). Detected from the role-framed banner content
 *  (the protocol carries no banner `kind`; PROTOCOL_VERSION is unchanged). */
function isStickyBanner(b: BannerInfo): boolean {
  return b.title === 'VETOED' || b.detail === 'PLAYED ON YOU';
}

/** v16 BOLT-072 — the hider's lock-anchor sound check ("turn it up — curses
 *  and airhorns need ears"). Play the tone, then confirm by ear. v17 BOLT-076:
 *  the deal no longer depends on it — this is a nag (and the WebAudio unlock),
 *  and the horn's own refund covers a phone that turns out to be silent. */
function SoundCheck({ onConfirm }: { onConfirm: () => void }) {
  const [step, setStep] = useState<'idle' | 'played' | 'confirmed'>('idle');
  if (step === 'confirmed')
    return (
      <span className="hider-note" data-testid="sound-check-done" style={{ margin: 0 }}>
        Sound checked ✓
      </span>
    );
  return (
    <div className="row" style={{ gap: 8, alignItems: 'center' }}>
      <span className="hider-note" style={{ margin: 0 }}>
        Turn it up. Hider cards and whistles need ears.
      </span>
      <button
        className="btn"
        data-testid="sound-check-play"
        onClick={() => {
          if (playConfirmTone()) setStep('played');
        }}
      >
        Test sound
      </button>
      {step === 'played' && (
        <button
          className="btn btn--signal"
          data-testid="sound-check-confirm"
          onClick={() => {
            onConfirm();
            setStep('confirmed');
          }}
        >
          I heard it
        </button>
      )}
    </div>
  );
}

function Banner({ b, lang, onDismiss }: { b: BannerInfo; lang: BabelLang | null; onDismiss: () => void }) {
  const sticky = isStickyBanner(b);
  return (
    <div
      className={`banner banner--${b.tone}${sticky ? ' banner--sticky' : ''}`}
      data-testid="banner"
      role={sticky ? 'button' : undefined}
      onClick={sticky ? onDismiss : undefined}
    >
      <span className="banner__title">{b.title}</span>
      <span className="banner__detail">{b.detail}</span>
      {sticky && <span className="banner__dismiss">{bt(lang, 'tapToDismiss')}</span>}
    </div>
  );
}

/** Surfaces a lapsed position (>15 s) or an opponent reconnecting (§Phase 4). */
function StaleBadge({ state }: { state: PublicState }) {
  const now = useServerClock(state.timer.serverNow); // no countdown — idles at ~1 Hz
  const pin = state.seekerPin;
  const stale = !!pin && now() - pin.ts > 15_000;
  const opp = state.you.role === 'hider' ? state.players.seeker : state.players.hider;
  const oppGone = !!opp && !opp.connected;
  if (!stale && !oppGone) return null;
  const label = oppGone
    ? 'Opponent reconnecting…'
    : state.you.role === 'seeker'
      ? 'Your GPS is stale'
      : 'Seeker GPS is stale';
  return (
    <div className="stale-badge" data-testid="stale-badge">
      <span className="dot dot--warn" />
      {label}
    </div>
  );
}

function HeadStartChip({ endsAt, serverNow }: { endsAt: number | null; serverNow: number }) {
  const now = useServerClock(serverNow, [endsAt]);
  const left = Math.max(0, (endsAt ?? now()) - now());
  return (
    <div className="headstart" data-testid="headstart">
      <span className="headstart__label">Head start</span>
      <span className="headstart__time">{formatCountdown(left)}</span>
    </div>
  );
}

function ThermoWatch({ endsAt, serverNow }: { endsAt: number; serverNow: number }) {
  const now = useServerClock(serverNow, [endsAt]);
  const left = Math.max(0, endsAt - now());
  return <span className="hider-note">Seeker is running a slice · {formatCountdown(left)}</span>;
}
